/**
 * OrganizerInsightsView — the club organizer's analytics surface.
 *
 * Reached via the "Insights" nav link (organizers only). It renders three
 * sections from GET /api/organizer/analytics, all scoped server-side to the
 * signed-in organizer's tenant:
 *   1. League progression velocity (progress bars per active league).
 *   2. Slow matchups — pending pairs holding up a round.
 *   3. Match competitiveness metric cards.
 *
 * Every section degrades to a graceful empty state, so an organizer with no
 * leagues / no reported matches sees guidance rather than a broken grid.
 *
 * Styling follows the project's two-skin model: night markup is base + `dark:`,
 * and `daylight:` classes are APPENDED for the light skin (never edited in).
 */

import { apiService } from "../services/api";
import { applyBaseLayout, setupHeaderListeners } from "../layouts/LayoutManager";
import { t } from "../i18n/config";
import { renderActivityTrendCard } from "../components/activityTrendChart";
import { renderDormantPlayersCard, wireNudgeButtons } from "../components/dormantPlayersCard";
import type {
  OrganizerAnalytics,
  LeagueVelocity,
  SlowMatchup,
  CompetitivenessIndex,
} from "shared";

export class OrganizerInsightsView {
  private container: HTMLElement;

  constructor(container: HTMLElement) {
    this.container = container;
  }

  async render(): Promise<void> {
    this.renderShell(`
      <div class="flex items-center justify-center gap-3 py-24 text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">
        <span class="w-5 h-5 rounded-full border-2 border-primary/25 border-t-primary daylight:border-day-ink/15 daylight:border-t-day-ink animate-spin" aria-hidden="true"></span> ${t("insights.loading")}
      </div>
    `);

    let body: string;
    try {
      const analytics = await apiService.getOrganizerAnalytics();
      body = this.renderBody(analytics);
    } catch (error) {
      console.error("[Insights] Failed to load organizer analytics:", error);
      body = `
        <div class="card text-center py-16">
          <span class="material-symbols-outlined text-5xl text-error mb-3 block">error_outline</span>
          <p class="text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft">${t("insights.loadError")}</p>
        </div>
      `;
    }

    this.renderShell(body);
    this.setupEventListeners();
  }

  /** Wrap content in the shared authenticated layout, with Insights active. */
  private renderShell(inner: string): void {
    this.container.innerHTML = applyBaseLayout({
      activePage: "insights",
      container: this.container,
      content: `
        <div class="mb-10">
          <h1 class="text-4xl lg:text-5xl font-black text-slate-900 dark:text-white daylight:text-day-ink mb-3">${t("insights.title")}</h1>
          <p class="text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft text-lg max-w-2xl">${t("insights.subtitle")}</p>
        </div>
        ${inner}
      `,
    });
  }

  private renderBody(analytics: OrganizerAnalytics): string {
    return `
      ${this.renderVelocitySection(analytics.leagueVelocity)}
      <div class="mt-10">
        ${renderActivityTrendCard(analytics.activityTrend)}
      </div>
      <div class="mt-10">
        ${renderDormantPlayersCard(analytics.dormantPlayers)}
      </div>
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-10">
        ${this.renderSlowMatchupsSection(analytics.slowMatchups)}
        ${this.renderCompetitivenessSection(analytics.competitiveness)}
      </div>
    `;
  }

  /** Per-league progress bars + rounds-per-week velocity. */
  private renderVelocitySection(velocity: LeagueVelocity[]): string {
    const heading = `
      <div class="flex items-center gap-2 mb-6">
        <span class="material-symbols-outlined text-primary daylight:text-day-ink">speed</span>
        <h2 class="text-2xl font-bold text-slate-900 dark:text-white daylight:text-day-ink">${t("insights.velocity.title")}</h2>
      </div>
    `;

    if (velocity.length === 0) {
      return `
        <section>
          ${heading}
          ${this.emptyState("trophy", t("insights.velocity.emptyTitle"), t("insights.velocity.emptyBody"))}
        </section>
      `;
    }

    const cards = velocity
      .map((league) => {
        const pct = Math.max(0, Math.min(100, league.progressPct));
        return `
        <div class="bg-white dark:bg-slate-800 daylight:bg-day-card p-5 rounded-lg shadow daylight:shadow-day-sm border border-slate-200 dark:border-slate-700 daylight:border-day-ink/10">
          <div class="flex items-baseline justify-between gap-3 mb-3">
            <h3 class="font-bold text-slate-900 dark:text-white daylight:text-day-ink truncate">${this.escape(league.leagueName)}</h3>
            <span class="text-sm font-mono font-bold text-slate-700 dark:text-slate-300 daylight:text-day-ink-soft shrink-0">${pct}%</span>
          </div>
          <div class="h-2.5 w-full rounded-full bg-slate-200 dark:bg-slate-700 daylight:bg-day-sunken overflow-hidden" role="progressbar" aria-valuenow="${pct}" aria-valuemin="0" aria-valuemax="100">
            <div class="h-full rounded-full bg-primary daylight:shadow-day-ball transition-all" style="width: ${pct}%"></div>
          </div>
          <div class="flex items-center justify-between mt-3 text-xs text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">
            <span>${t("insights.velocity.roundsCompleted", { completed: league.completedRounds, total: league.totalRounds })}</span>
            <span class="font-medium">${t("insights.velocity.perWeek", { count: league.roundsPerWeek })}</span>
          </div>
        </div>`;
      })
      .join("");

    return `
      <section>
        ${heading}
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          ${cards}
        </div>
      </section>
    `;
  }

  /** Pending matchups delaying rounds, oldest first. */
  private renderSlowMatchupsSection(matchups: SlowMatchup[]): string {
    const heading = `
      <div class="flex items-center gap-2 mb-6">
        <span class="material-symbols-outlined text-warning daylight:text-day-warning">hourglass_top</span>
        <h2 class="text-2xl font-bold text-slate-900 dark:text-white daylight:text-day-ink">${t("insights.slow.title")}</h2>
      </div>
    `;

    const inner =
      matchups.length === 0
        ? this.emptyState("check_circle", t("insights.slow.emptyTitle"), t("insights.slow.emptyBody"))
        : `
          <ul class="space-y-3">
            ${matchups
              .map(
                (m) => `
              <li class="flex items-center justify-between gap-3 bg-slate-50 dark:bg-slate-800/50 daylight:bg-day-sunken rounded-lg px-4 py-3">
                <div class="min-w-0">
                  <p class="font-bold text-slate-900 dark:text-white daylight:text-day-ink truncate">${this.escape(m.sideA || t("insights.slow.tbd"))} <span class="text-slate-400 daylight:text-day-ink-muted font-normal">${t("insights.slow.vs")}</span> ${this.escape(m.sideB || t("insights.slow.tbd"))}</p>
                  <p class="text-xs text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted truncate">${this.escape(m.leagueName)} · ${t("insights.slow.round", { number: m.roundNumber })}</p>
                </div>
                <span class="shrink-0 badge badge-warning">${t("insights.slow.pending")}</span>
              </li>`,
              )
              .join("")}
          </ul>
        `;

    return `
      <section>
        ${heading}
        <div class="card">${inner}</div>
      </section>
    `;
  }

  /** Competitiveness metric cards. */
  private renderCompetitivenessSection(c: CompetitivenessIndex): string {
    const heading = `
      <div class="flex items-center gap-2 mb-6">
        <span class="material-symbols-outlined text-success daylight:text-day-success">balance</span>
        <h2 class="text-2xl font-bold text-slate-900 dark:text-white daylight:text-day-ink">${t("insights.comp.title")}</h2>
      </div>
    `;

    if (c.reportedMatches === 0) {
      return `
        <section>
          ${heading}
          <div class="card">${this.emptyState("scoreboard", t("insights.comp.emptyTitle"), t("insights.comp.emptyBody"))}</div>
        </section>
      `;
    }

    return `
      <section>
        ${heading}
        <div class="grid grid-cols-2 gap-4">
          ${this.metricCard(t("insights.comp.closeMatches"), `${c.closeMatchRate}%`, t("insights.comp.closeMatchesSub", { count: c.closeMatches, total: c.reportedMatches }))}
          ${this.metricCard(t("insights.comp.threeSets"), `${c.threeSetRate}%`, t("insights.comp.threeSetsSub", { count: c.threeSetMatches, total: c.reportedMatches }))}
          ${this.metricCard(t("insights.comp.avgMargin"), `${c.avgGameMargin}`, t("insights.comp.avgMarginSub"))}
          ${this.metricCard(t("insights.comp.reported"), `${c.reportedMatches}`, t("insights.comp.reportedSub"))}
        </div>
      </section>
    `;
  }

  private metricCard(label: string, value: string, sub: string): string {
    return `
      <div class="bg-white dark:bg-slate-800 daylight:bg-day-card p-4 rounded-lg shadow daylight:shadow-day-sm border border-slate-200 dark:border-slate-700 daylight:border-day-ink/10">
        <p class="text-sm font-medium text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">${this.escape(label)}</p>
        <p class="text-3xl font-black text-slate-900 dark:text-white daylight:text-day-ink mt-1">${this.escape(value)}</p>
        <p class="text-xs text-slate-400 dark:text-slate-500 daylight:text-day-ink-muted mt-1">${this.escape(sub)}</p>
      </div>
    `;
  }

  private emptyState(icon: string, title: string, body: string): string {
    return `
      <div class="text-center py-10">
        <span class="material-symbols-outlined text-5xl text-slate-300 dark:text-slate-700 daylight:text-day-ink-muted flex justify-center mb-3">${icon}</span>
        <h3 class="text-lg font-bold text-slate-900 dark:text-white daylight:text-day-ink mb-1">${this.escape(title)}</h3>
        <p class="text-sm text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft max-w-sm mx-auto">${this.escape(body)}</p>
      </div>
    `;
  }

  private setupEventListeners(): void {
    wireNudgeButtons(this.container);
    setupHeaderListeners(this.container, {
      onCreateLeague: () => {
        window.location.hash = "#home/new";
      },
      onLogout: () => {
        localStorage.removeItem("authToken");
        localStorage.removeItem("userRole");
        window.location.hash = "#login";
      },
    });
  }

  private escape(value: string): string {
    const div = document.createElement("div");
    div.textContent = value ?? "";
    return div.innerHTML;
  }
}
